import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { commissionsSettingsApi } from "../../../modules/commissions/settings/api";
import {
  AssignCommissionsSettingsLevel,
  AssignCommissionsSettingsRank,
  CommissionsSettings,
  CreateCommissionsSettingsRule,
  UpdateCommissionsSettingsRule
} from "../../../modules/commissions/settings/types";
import type { UUID } from "../../../typings/global";
import { EntityObject, OptimisticLockVersion, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";
import messageUtils from "../../utils/messageUtils";
import { openBlobFile } from "../../utils/utils";

export const COMMISSIONS_SETTINGS_QUERY_KEYS = {
  getCommissionsSettings: (id: UUID) => ["getCommissionsSettings", id],
  getProfileCommissionsSettings: (id: UUID) => ["getProfileCommissionsSettings", id],
  assignCommissionsSettingsLevel: () => ["assignCommissionsSettingsLevel"],
  unassignCommissionsSettingsLevel: () => ["unassignCommissionsSettingsLevel"],
  assignCommissionsSettingsRank: () => ["assignCommissionsSettingsRank"],
  unassignCommissionsSettingsRank: () => ["unassignCommissionsSettingsRank"],
  togglePaymentInclusionCommissionsSettings: () => ["togglePaymentInclusionCommissionsSettings"],
  toggleUsageAsHeadCommissionsSettings: () => ["toggleUsageAsHeadCommissionsSettings"],
  togglePointsUnificationCommissionsSettings: () => ["togglePointsUnificationCommissionsSettings"],
  createCommissionsSettingsRule: () => ["createCommissionsSettingsRule"],
  updateCommissionsSettingsRule: () => ["updateCommissionsSettingsRule"],
  deleteCommissionsSettingsRule: () => ["deleteCommissionsSettingsRule"],
  uploadCommissionsSettingsAttachments: () => ["uploadCommissionsSettingsAttachments"],
  downloadCommissionsSettingsAttachments: (params?: TwoLevelEntityIdObject) =>
    params ? ["downloadCommissionsSettingsAttachments", params] : ["downloadCommissionsSettingsAttachments"],
  deleteCommissionsSettingsAttachments: () => ["deleteCommissionsSettingsAttachments"]
};

export const useGetCommissionsSettings = (id?: UUID, enabled?: boolean) => {
  return useQuery({
    queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(id!),
    queryFn: async () => {
      const { data } = await commissionsSettingsApi.getCommissionsSettings({ id: id! });

      return data;
    },
    enabled
  });
};

export const useGetProfileCommissionsSettings = (id?: UUID) => {
  return useQuery({
    queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getProfileCommissionsSettings(id!),
    queryFn: async () => {
      const { data } = await commissionsSettingsApi.getProfileCommissionsSettings({ id: id! });

      return data;
    },
    enabled: !!id
  });
};

export const useAssignCommissionsSettingsLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.assignCommissionsSettingsLevel(),
    mutationFn: async (payload: EntityObject<AssignCommissionsSettingsLevel>) => {
      const { data } = await commissionsSettingsApi.assignCommissionsSettingsLevel(payload);

      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useUnassignCommissionsSettingsLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.unassignCommissionsSettingsLevel(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.unassignCommissionsSettingsLevel(payload);

      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useAssignCommissionsSettingRank = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.assignCommissionsSettingsRank(),
    mutationFn: async (payload: EntityObject<AssignCommissionsSettingsRank>) => {
      const { data } = await commissionsSettingsApi.assignCommissionsSettingsRank(payload);

      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useUnassignCommissionsSettingRank = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.unassignCommissionsSettingsRank(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.unassignCommissionsSettingsRank(payload);

      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useTogglePaymentInclusionCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.togglePaymentInclusionCommissionsSettings(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.togglePaymentInclusionCommissionsSettings(payload);

      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useToggleUsageAsHeadCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.toggleUsageAsHeadCommissionsSettings(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.toggleUsageAsHeadCommissionsSettings(payload);

      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useTogglePointsUnificationCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.togglePointsUnificationCommissionsSettings(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.togglePointsUnificationCommissionsSettings(payload);

      return data;
    },
    onSuccess: data => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useCreateCommissionsSettingsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.createCommissionsSettingsRule(),
    mutationFn: async (payload: EntityObject<CreateCommissionsSettingsRule>) => {
      const { data } = await commissionsSettingsApi.createCommissionsSettingsRule(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id),
        (oldData: CommissionsSettings) => ({ ...oldData, rules: [...oldData.rules, data] })
      );
      messageUtils.itemCreatedNotification();
    }
  });
};

export const useUpdateCommissionsSettingsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.updateCommissionsSettingsRule(),
    mutationFn: async (payload: TwoLevelEntityObject<UpdateCommissionsSettingsRule>) => {
      const { data } = await commissionsSettingsApi.updateCommissionsSettingsRule(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id1),
        (oldData: CommissionsSettings) => ({
          ...oldData,
          rules: [...oldData.rules.filter(rule => rule.id !== mutatedPayload.id2), data]
        })
      );
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useDeleteCommissionsSettingsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.deleteCommissionsSettingsRule(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      return commissionsSettingsApi.deleteCommissionsSettingsRule(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id1),
        (oldData: CommissionsSettings) => ({
          ...oldData,
          rules: oldData.rules.filter(rule => rule.id !== mutatedPayload.id2)
        })
      );
      messageUtils.itemDeletedNotification();
    }
  });
};

export const useUploadCommissionsSettingsAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.uploadCommissionsSettingsAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await commissionsSettingsApi.uploadCommissionsSettingsAttachments(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id),
        (oldData: CommissionsSettings) => ({ ...oldData, attachments: data })
      );

      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getProfileCommissionsSettings(mutatedPayload.id),
        (oldData: CommissionsSettings) => ({ ...oldData, attachments: data })
      );
    }
  });
};

export const useDownloadCommissionsSettingsAttachment = () => {
  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.downloadCommissionsSettingsAttachments(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      const response = await commissionsSettingsApi.downloadCommissionsSettingsAttachment(payload);
      openBlobFile(response);

      return response.data;
    }
  });
};

export const useDeleteCommissionsSettingsAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.deleteCommissionsSettingsAttachments(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      return commissionsSettingsApi.deleteCommissionsSettingsAttachment(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id1),
        (oldData: CommissionsSettings) => ({
          ...oldData,
          attachments: oldData.attachments.filter(item => item.id !== mutatedPayload.id2)
        })
      );

      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getProfileCommissionsSettings(mutatedPayload.id1),
        (oldData: CommissionsSettings) => ({
          ...oldData,
          attachments: oldData.attachments.filter(item => item.id !== mutatedPayload.id2)
        })
      );
    }
  });
};
